import { NavLink } from 'react-router-dom';
import { Box, Button, Divider, Drawer, IconButton, Stack, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import { useTenant } from '../../../contexts/TenantContext';
import { ADMIN_SECTIONS, SIDEBAR_WIDTH, TOPBAR_HEIGHT } from './adminSections';
import { useStaff } from '../StaffContext';
import useAdminButton from '../hooks/useAdminButton';

const itemSx = {
  justifyContent: 'flex-start',
  width: '100%',
  px: 2,
  py: 1,
  borderRadius: 2,
  fontSize: '0.875rem',
  fontWeight: 600,
  textTransform: 'none',
};

/**
 * One menu entry. NavLink marks the current page with the `active` class, and the entry is
 * filled as the dashboard's buttons are then (useAdminButton).
 */
const NavItem = ({ section, onNavigate }) => {
  const { to, end, label, Icon, testId } = section;
  const { filledSx } = useAdminButton();

  return (
    <Button
      component={NavLink}
      to={to}
      end={end}
      onClick={onNavigate}
      data-testid={testId}
      startIcon={<Icon fontSize="small" />}
      sx={{
        ...itemSx,
        color: 'text.primary',
        '&:hover': { bgcolor: 'action.hover' },
        '&.active': filledSx,
      }}
    >
      {label}
    </Button>
  );
};

const SidebarContent = ({ onNavigate }) => {
  const tenant = useTenant();
  const { isAdmin } = useStaff();
  const sections = ADMIN_SECTIONS
    .filter((section) => !section.tenantFlag || tenant[section.tenantFlag])
    .filter((section) => !section.adminOnly || isAdmin);

  return (
    <>
      <Stack
        component="nav"
        spacing={1}
        // A faint rule between the entries, so each reads as its own place.
        divider={<Divider flexItem sx={{ borderColor: 'grey.200' }} />}
        sx={{ flex: 1, p: 2 }}
      >
        {sections.map((section) => (
          <NavItem key={section.to} section={section} onNavigate={onNavigate} />
        ))}
      </Stack>
    </>
  );
};

/**
 * The admin menu: an aside that stays beside the page on a wide screen, a drawer behind the top
 * bar's menu button on anything narrower. Only one of the two is rendered, so no menu entry is
 * in the page twice. On a phone the drawer also carries „Vendég oldal” in its bottom left corner,
 * which the top bar has no room for there.
 */
const AdminSidebar = ({ isPermanent, showGuestSiteButton, mobileOpen, onMobileClose }) => {
  if (isPermanent) {
    return (
      <Box
        component="aside"
        sx={{
          width: SIDEBAR_WIDTH,
          flexShrink: 0,
          bgcolor: '#fff',
          borderRight: 1,
          borderRightColor: 'divider',
          display: 'flex',
          flexDirection: 'column',
          position: 'sticky',
          top: TOPBAR_HEIGHT,
          alignSelf: 'flex-start',
          height: `calc(100vh - ${TOPBAR_HEIGHT}px)`,
        }}
      >
        <SidebarContent />
      </Box>
    );
  }

  return (
    <Drawer
      open={mobileOpen}
      onClose={onMobileClose}
      PaperProps={{ sx: { width: SIDEBAR_WIDTH, display: 'flex', flexDirection: 'column' } }}
    >
      <Box
        sx={{
          height: TOPBAR_HEIGHT,
          flexShrink: 0,
          px: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: 1,
          borderBottomColor: 'divider',
        }}
      >
        <Typography sx={{ fontSize: '1rem', fontWeight: 700 }}>Admin felület</Typography>
        <IconButton onClick={onMobileClose} size="small" aria-label="Menü bezárása">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <SidebarContent onNavigate={onMobileClose} />

      {showGuestSiteButton && (
        <Box sx={{ flexShrink: 0, p: 2, display: 'flex', justifyContent: 'flex-start' }}>
          <Button
            component={NavLink}
            to="/"
            onClick={onMobileClose}
            data-testid="admin-guest-site"
            variant="outlined"
            size="small"
            startIcon={<ArrowBackIcon fontSize="small" />}
            sx={{
              color: 'text.primary',
              borderColor: 'grey.400',
              textTransform: 'none',
              whiteSpace: 'nowrap',
              '&:hover': { borderColor: 'text.primary', bgcolor: 'action.hover' },
            }}
          >
            Vendég oldal
          </Button>
        </Box>
      )}
    </Drawer>
  );
};

export default AdminSidebar;
